import React, { useState, useEffect } from 'react';
import { 
  Brain, Loader2, Search, Download, Eye, Check, X,
  AlertTriangle, AlertCircle, CheckCircle, Users, FileText
} from 'lucide-react';
import { adminSuicideTestAPI } from '../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const riskColors = {
  low: { bg: '#d1fae5', color: '#059669', icon: CheckCircle },
  moderate: { bg: '#fef3c7', color: '#d97706', icon: AlertCircle },
  high: { bg: '#fed7aa', color: '#ea580c', icon: AlertTriangle },
  critical: { bg: '#fee2e2', color: '#dc2626', icon: AlertTriangle }
};

const SuicideTestPage = () => {
  const [results, setResults] = useState([]);
  const [groups, setGroups] = useState([]);
  const [riskLevels, setRiskLevels] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [filterRisk, setFilterRisk] = useState('');
  const [filterReviewed, setFilterReviewed] = useState('');
  
  const [selectedResult, setSelectedResult] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchResults();
  }, [filterGroup, filterRisk, filterReviewed]);

  const fetchResults = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterGroup) params.group = filterGroup;
      if (filterRisk) params.risk_level = filterRisk;
      if (filterReviewed) params.reviewed = filterReviewed;
      if (searchTerm) params.search = searchTerm;
      
      const response = await adminSuicideTestAPI.getAll(params);
      setResults(response.data.results || []);
      setGroups(response.data.groups || []);
      setRiskLevels(response.data.risk_levels || []);
      setStats(response.data.stats || null);
    } catch (error) {
      console.error('Xato:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchResults();
  };

  const openDetail = async (result) => {
    setSelectedResult(result);
    setNotes(result.notes || '');
    setShowDetailModal(true);
    setDetailLoading(true);
    
    try {
      const response = await adminSuicideTestAPI.get(result.id);
      setQuestions(response.data.questions || []);
    } catch (error) {
      console.error('Xato:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleReview = async (reviewed) => {
    if (!selectedResult) return;
    setSaving(true);
    
    try {
      await adminSuicideTestAPI.update(selectedResult.id, { reviewed, notes });
      await fetchResults();
      setSelectedResult(prev => ({ ...prev, reviewed, notes }));
    } catch (error) {
      alert('Xatolik yuz berdi');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = () => {
    let url = `${API_URL}/api/admin/suicide-test/export/?`;
    if (filterGroup) url += `group=${filterGroup}&`;
    if (filterRisk) url += `risk_level=${filterRisk}`;
    window.open(url, '_blank');
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('uz-UZ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAnswerText = (answer) => {
    const map = { yes: 'Ha', no: "Yo'q", unknown: 'Bilmayman' };
    return map[answer] || answer;
  };

  const getAnswerScore = (answer) => {
    const map = { yes: 3, no: 1, unknown: 2 };
    return map[answer] || 0;
  };

  return (
    <div className="page-container">
      <header className="page-header">
        <div className="page-title">
          <h1><Brain size={28} /> Psixologik test natijalari</h1>
          <p>Suitsidga moyillikni aniqlash testi</p>
        </div>
        <button className="btn btn-success" onClick={handleExport}>
          <Download size={18} />
          Excel export
        </button>
      </header>

      {/* Statistika */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon total"><Users size={24} /></div>
            <div className="stat-info">
              <span className="stat-value">{stats.total}</span>
              <span className="stat-label">Jami test</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#d1fae520', color: '#059669' }}>
              <CheckCircle size={24} />
            </div>
            <div className="stat-info">
              <span className="stat-value">{stats.low}</span>
              <span className="stat-label">Xavfsiz</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#fef3c720', color: '#d97706' }}>
              <AlertCircle size={24} />
            </div>
            <div className="stat-info">
              <span className="stat-value">{stats.moderate}</span>
              <span className="stat-label">E'tibortalab</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#fed7aa20', color: '#ea580c' }}>
              <AlertTriangle size={24} />
            </div>
            <div className="stat-info">
              <span className="stat-value">{stats.high}</span>
              <span className="stat-label">Ta'sirchan</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#fee2e220', color: '#dc2626' }}>
              <AlertTriangle size={24} />
            </div>
            <div className="stat-info">
              <span className="stat-value">{stats.critical}</span>
              <span className="stat-label">Kritik</span>
            </div>
          </div>
        </div>
      )}

      {/* Filterlar */}
      <div className="filters-row">
        <form onSubmit={handleSearch} className="search-bar">
          <Search size={20} />
          <input 
            type="text" 
            placeholder="Talaba qidirish..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </form>
        <select 
          className="filter-select"
          value={filterGroup}
          onChange={(e) => setFilterGroup(e.target.value)}
        >
          <option value="">Barcha guruhlar</option>
          {groups.map(g => (
            <option key={g.id} value={g.id}>{g.name}</option>
          ))}
        </select>
        <select 
          className="filter-select"
          value={filterRisk}
          onChange={(e) => setFilterRisk(e.target.value)}
        >
          <option value="">Barcha darajalar</option>
          {riskLevels.map(r => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
        <select 
          className="filter-select"
          value={filterReviewed}
          onChange={(e) => setFilterReviewed(e.target.value)}
        >
          <option value="">Barchasi</option>
          <option value="true">Ko'rib chiqilgan</option>
          <option value="false">Ko'rib chiqilmagan</option>
        </select>
      </div>

      {/* Jadval */}
      {loading ? (
        <div className="loading-container">
          <Loader2 className="spinner" size={40} />
        </div>
      ) : results.length === 0 ? (
        <div className="empty-state">
          <FileText size={48} />
          <p>Natijalar topilmadi</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>№</th>
                <th>Talaba</th>
                <th>Guruh</th>
                <th>Ball</th>
                <th>Xavf darajasi</th>
                <th>Sana</th>
                <th>Holat</th>
                <th>Amallar</th>
              </tr>
            </thead>
            <tbody>
              {results.map((result, idx) => {
                const riskStyle = riskColors[result.risk_level] || riskColors.low;
                const RiskIcon = riskStyle.icon;
                
                return (
                  <tr key={result.id}>
                    <td>{idx + 1}</td>
                    <td>
                      <div>
                        <strong>{result.student_name}</strong>
                        <br />
                        <small style={{ color: '#64748b' }}>{result.student_id}</small>
                      </div>
                    </td>
                    <td>{result.group_name || '-'}</td>
                    <td>
                      <span style={{ 
                        fontWeight: '600', 
                        color: riskStyle.color 
                      }}>
                        {result.total_score}
                      </span>
                    </td>
                    <td>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 12px',
                        borderRadius: '20px',
                        background: riskStyle.bg,
                        color: riskStyle.color,
                        fontSize: '13px',
                        fontWeight: '500'
                      }}>
                        <RiskIcon size={16} />
                        {result.risk_level_display?.split(' ')[0]}
                      </span>
                    </td>
                    <td style={{ color: '#64748b', fontSize: '13px' }}>
                      {formatDate(result.completed_at)}
                    </td>
                    <td>
                      {result.reviewed ? (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          color: '#10b981',
                          fontSize: '13px'
                        }}>
                          <Check size={16} /> Ko'rilgan
                        </span>
                      ) : (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          color: '#f59e0b',
                          fontSize: '13px'
                        }}>
                          <AlertCircle size={16} /> Kutilmoqda
                        </span>
                      )}
                    </td>
                    <td>
                      <button 
                        className="btn btn-icon"
                        style={{ background: '#f1f5f9', color: '#475569' }}
                        onClick={() => openDetail(result)}
                        title="Batafsil ko'rish"
                      >
                        <Eye size={18} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Tafsilot Modal */}
      {showDetailModal && selectedResult && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div 
            className="modal" 
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }}
          >
            <div className="modal-header">
              <h2>Test natijasi - {selectedResult.student_name}</h2>
              <button className="close-btn" onClick={() => setShowDetailModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              {/* Talaba va natija ma'lumotlari */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: '20px',
                marginBottom: '24px'
              }}>
                <div className="card" style={{ margin: 0 }}>
                  <div className="card-header"><h3>Talaba</h3></div>
                  <div className="card-body">
                    <p><strong>F.I.Sh:</strong> {selectedResult.student_name}</p>
                    <p><strong>ID:</strong> {selectedResult.student_id}</p>
                    <p><strong>Guruh:</strong> {selectedResult.group_name || '-'}</p>
                    <p><strong>Sana:</strong> {formatDate(selectedResult.completed_at)}</p>
                  </div>
                </div>
                <div className="card" style={{ margin: 0 }}>
                  <div className="card-header"><h3>Natija</h3></div>
                  <div className="card-body">
                    <p style={{ fontSize: '32px', fontWeight: '700', color: riskColors[selectedResult.risk_level]?.color }}>
                      {selectedResult.total_score} ball
                    </p>
                    <p style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 16px',
                      borderRadius: '25px',
                      background: riskColors[selectedResult.risk_level]?.bg,
                      color: riskColors[selectedResult.risk_level]?.color,
                      fontWeight: '500'
                    }}>
                      {selectedResult.risk_level_display}
                    </p>
                  </div>
                </div>
              </div>

              {/* Javoblar */}
              {detailLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Loader2 className="spinner" size={32} />
                </div>
              ) : (
                <div className="card" style={{ margin: 0, marginBottom: '24px' }}>
                  <div className="card-header"><h3>Javoblar</h3></div>
                  <div className="card-body" style={{ padding: 0 }}>
                    <table className="data-table" style={{ margin: 0 }}>
                      <thead>
                        <tr>
                          <th style={{ width: '50px' }}>№</th>
                          <th>Savol</th>
                          <th style={{ width: '120px' }}>Javob</th>
                          <th style={{ width: '80px' }}>Ball</th>
                        </tr>
                      </thead>
                      <tbody>
                        {questions.map(q => {
                          const answer = selectedResult.answers?.[q.id];
                          return (
                            <tr key={q.id}>
                              <td>{q.question_number}</td>
                              <td>{q.question_text}</td>
                              <td>
                                <span style={{
                                  padding: '4px 12px',
                                  borderRadius: '12px',
                                  background: answer === 'yes' ? '#d1fae5' : answer === 'no' ? '#fee2e2' : '#fef3c7',
                                  color: answer === 'yes' ? '#059669' : answer === 'no' ? '#dc2626' : '#d97706',
                                  fontSize: '13px',
                                  fontWeight: '500'
                                }}>
                                  {getAnswerText(answer)}
                                </span>
                              </td>
                              <td style={{ fontWeight: '600' }}>{getAnswerScore(answer)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Izoh */}
              <div className="form-group">
                <label>Psixolog izohi</label>
                <textarea 
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Izoh yozing..."
                  rows={3}
                />
              </div>
            </div>
            <div className="modal-footer">
              {selectedResult.reviewed ? (
                <button 
                  className="btn btn-warning"
                  onClick={() => handleReview(false)}
                  disabled={saving}
                >
                  {saving ? <Loader2 className="spinner" size={18} /> : <X size={18} />}
                  Bekor qilish
                </button>
              ) : (
                <button 
                  className="btn btn-success"
                  onClick={() => handleReview(true)}
                  disabled={saving}
                >
                  {saving ? <Loader2 className="spinner" size={18} /> : <Check size={18} />}
                  Ko'rib chiqildi
                </button>
              )}
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowDetailModal(false)}
              >
                Yopish
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuicideTestPage;
